"use client";

import EventLink from "@components/links/eventLink";
import LoadingSpinner from "@components/loadingSpinner";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { fetcher } from "@utils/fetchers";
import { usePrevious } from "@utils/hooks";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { SyntheticEvent, useCallback } from "react";
import useSWR from "swr";
import { Filter, LandingPageLink } from "@features/search";
import { AnalyticsPage } from "@components/analytics";
import { HOST } from "@utils/constants";
import { useTranslations } from "next-intl";

interface Event {
  Name: string;
  Url: string;
  Header: string;
  Region: string;
  Date: { Start: string | null; End: string | null; Range: string | null };
  Images: { BannerSmall: string; DefaultSmall: string; Portrait: string };
}

interface LandingPage extends Event {
  Footer: string;
  Images: {
    BannerSmall: string;
    DefaultSmall: string;
    Landingpage: string;
    Portrait: string;
  };
}

interface FacetItem {
  QKey: string;
  QValue: string;
  Label: string;
  Count: number;
}

interface Facet {
  Label: string;
  Items: FacetItem[];
}

interface Filter {
  Group: string;
  Key: string;
  Value: string;
  Label: string;
}

interface SearchResults {
  Events: Event[];
  LandingPages: LandingPage[];
  Facets: Facet[];
  Filters: Filter[];
  Paging: {
    PageCount: number;
    PageNumber: number;
  };
  Query: string;
}

export default function SearchPage() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const q = searchParams.get("q");
  const facetParams = searchParams.toString().split("&").slice(1).join("&");
  const { data: results, isLoading } = useSWR<SearchResults>(
    q ? `search/full/${q}?PageSize=${100}&${facetParams}` : null,
    fetcher
  );
  const previousResults = usePrevious(results).current;
  const resultsList = results ?? previousResults;
  const t = useTranslations("Search");

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(
        searchParams as unknown as URLSearchParams
      );
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }

      const paramsString = params.toString();

      return paramsString ? `?${paramsString}` : "";
    },
    [searchParams]
  );

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const { search } = Object.fromEntries(new FormData(event.currentTarget));

    router.push(pathname + createQueryString("q", search as string));
  }

  function getRelativePath(url: string) {
    const rel_path = `/events/${url.split("/events/")[1]}`;

    return rel_path;
  }

  return (
    <div className="p-6">
      <h2 className="text-3xl sm:text-5xl font-black text-black">
        {t("search")}
      </h2>
      <div className="flex flex-col gap-8 mt-8">
        <div className="flex flex-col gap-4">
          <div className="relative flex flex-col lg:flex-row gap-4 lg:items-center">
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <div className="relative flex items-center">
                <input
                  aria-label="search-input"
                  key={q}
                  name="search"
                  defaultValue={q ?? ""}
                  className="w-full lg:w-96 pl-7 pr-2 bg-white rounded-md border border-gray-300 px-2 py-1.5 text-black focus:outline-none focus:border-2 focus:py-[5px] focus:pl-[27px] focus:border-indigo-500"
                />
                <MagnifyingGlassIcon className="absolute left-2 text-black w-4 h-4" />
              </div>
              <button
                name="submit"
                type="submit"
                className="h-fit px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-300 text-white font-semibold transition-colors duration-200 ease-in-out"
              >
                {t("search")}
              </button>
            </form>
            <div className="flex py-2 lg:justify-end w-full overflow-x-auto lg:overflow-x-hidden">
              {resultsList ? (
                <Filter
                  values={resultsList.Filters}
                  options={resultsList?.Facets.filter(
                    (facet) => facet.Label !== "Type"
                  )}
                  onAdd={(option) => {
                    const prev = searchParams.get(option.QKey);
                    router.push(
                      pathname +
                        createQueryString(
                          option.QKey,
                          prev ? `${prev},${option.QValue}` : option.QValue
                        )
                    );
                  }}
                  onRemove={(option) => {
                    const prev = searchParams.get(option.QKey);
                    router.push(
                      pathname +
                        createQueryString(
                          option.QKey,
                          prev
                            ?.split(",")
                            .filter((value) => value !== option.QValue)
                            .join(",") ?? ""
                        )
                    );
                  }}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
          {resultsList ? (
            <div className="flex gap-2 flex-wrap">
              {resultsList.Filters.map((filter) => (
                <span
                  key={filter.Label}
                  className="flex items-center gap-1 py-1 px-2 rounded-lg bg-indigo-100 text-indigo-800 text-sm"
                >
                  {filter.Label}
                  <button
                    onClick={() => {
                      const prev = searchParams.get(filter.Key);
                      router.push(
                        pathname +
                          createQueryString(
                            filter.Key,
                            prev
                              ?.split(",")
                              .filter((value) => value !== filter.Value)
                              .join(",") ?? ""
                          )
                      );
                    }}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <></>
          )}
        </div>
        {isLoading ? (
          <div className="flex w-full justify-center items-center">
            <LoadingSpinner size="48" />
          </div>
        ) : (
          <></>
        )}
        {results && results.LandingPages.length > 0 ? (
          <div className="flex flex-col gap-4">
            <h3 className="text-2xl sm:text-5xl font-black text-black">
              {t("eventseries")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {results.LandingPages.map((page) => (
                <LandingPageLink key={page.Name} landingPage={page} />
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        {results && results.Events.length > 0 ? (
          <div className="flex flex-col gap-4">
            <h3 className="text-2xl sm:text-5xl font-black text-black">
              {t("events")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-8">
              {results.Events.map((event) => (
                <EventLink
                  key={`${event.Name}-${event.Region}`}
                  event={{
                    ...event,
                    Byline: event.Header,
                    Url: getRelativePath(event.Url),
                  }}
                  showEventRegions={true}
                />
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        {!isLoading &&
        (!results?.Events || results.Events.length === 0) &&
        (!results?.LandingPages || results.LandingPages.length === 0) ? (
          <p className="text-lg font-semibold text-black">{t("noresult")}</p>
        ) : (
          <></>
        )}
      </div>
      <AnalyticsPage
        properties={{
          path: "/search",
          title: "Search",
          url: `${HOST}/search`,
          search: `${q ? `?=${q}` : ""}`,
        }}
      />
    </div>
  );
}
